import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Edit, Trash2, Eye, EyeOff, BookOpen, Upload, X,
  DollarSign, Download, TrendingUp, ShoppingCart, FileText
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

// Stat Card Component
function StatCard({ title, value, icon: Icon, color = "cyan" }) {
  const colorClasses = {
    cyan: "bg-cyan-50 text-cyan-600 border-cyan-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-200",
    amber: "bg-amber-50 text-amber-600 border-amber-200"
  };

  return (
    <div className="bg-white border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg border ${colorClasses[color]}`}>
          <Icon size={18} />
        </div>
        <div>
          <p className="text-2xl font-outfit font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500 font-dm-sans">{title}</p>
        </div>
      </div>
    </div>
  );
}

// Book Card Component
function BookCard({ book, onEdit, onDelete, onToggleVisibility }) {
  const discountedPrice = book.discount_percentage > 0 
    ? Math.round(book.price * (1 - book.discount_percentage / 100))
    : book.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 overflow-hidden hover:shadow-md transition-all"
    >
      {/* Cover Image */}
      <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-50 relative overflow-hidden">
        <img 
          src={`${API}/books/${book.id}/cover`}
          alt={book.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {/* Visibility Badge */}
        <div className={`absolute top-2 right-2 px-2 py-1 text-xs font-bold ${book.is_visible ? 'bg-emerald-500' : 'bg-gray-500'} text-white`}>
          {book.is_visible ? 'Visible' : 'Hidden'}
        </div>
        {/* Discount Badge */}
        {book.discount_percentage > 0 && (
          <div className="absolute top-2 left-2 px-2 py-1 text-xs font-bold bg-red-500 text-white">
            {book.discount_percentage}% OFF
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-outfit font-bold text-gray-900 text-lg line-clamp-1">{book.title}</h3>
        <p className="text-sm text-gray-500 font-dm-sans mb-2">by {book.author}</p>
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{book.description}</p>
        
        {/* Price */}
        <div className="flex items-center gap-2 mb-3">
          {book.discount_percentage > 0 ? (
            <>
              <span className="text-xl font-bold text-emerald-600 font-outfit">₹{discountedPrice}</span>
              <span className="text-sm text-gray-400 line-through">₹{book.price}</span>
            </>
          ) : (
            <span className="text-xl font-bold text-gray-900 font-outfit">₹{book.price}</span>
          )}
        </div>
        
        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <div className="flex items-center gap-1">
            <ShoppingCart size={14} />
            <span>{book.total_sales || 0} sales</span>
          </div>
          <div className="flex items-center gap-1">
            <DollarSign size={14} />
            <span>₹{book.total_revenue || 0}</span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => onToggleVisibility(book)}
            className={`flex-1 py-2 text-sm font-bold flex items-center justify-center gap-1 ${book.is_visible ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
          >
            {book.is_visible ? <EyeOff size={14} /> : <Eye size={14} />}
            {book.is_visible ? 'Hide' : 'Show'}
          </button>
          <button
            onClick={() => onEdit(book)}
            className="flex-1 py-2 text-sm font-bold bg-cyan-100 text-cyan-700 hover:bg-cyan-200 flex items-center justify-center gap-1"
          >
            <Edit size={14} />
            Edit
          </button>
          <button
            onClick={() => onDelete(book.id)}
            className="py-2 px-3 text-sm font-bold bg-red-100 text-red-700 hover:bg-red-200"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function AdminBooks() {
  const [books, setBooks] = useState([]);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editBook, setEditBook] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  
  const [form, setForm] = useState({
    title: "",
    author: "",
    description: "",
    price: "",
    discount_percentage: "0",
    is_visible: true,
    cover_image: null,
    pdf_file: null
  });

  const fetchBooks = async () => {
    try {
      const booksRes = await axios.get(`${API}/admin/books`, authHeader());
      setBooks(booksRes.data);
      // Fetch sales analytics separately - don't let it block books loading
      try {
        const salesRes = await axios.get(`${API}/admin/book-purchases/analytics`, authHeader());
        setSales(salesRes.data);
      } catch (salesErr) {
        console.warn("Could not fetch sales analytics:", salesErr);
      }
    } catch (err) {
      console.error("Failed to fetch books:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBooks(); }, []);

  const openCreate = () => {
    setEditBook(null);
    setForm({
      title: "",
      author: "",
      description: "",
      price: "",
      discount_percentage: "0",
      is_visible: true,
      cover_image: null,
      pdf_file: null
    });
    setError("");
    setShowModal(true);
  };

  const openEdit = (book) => {
    setEditBook(book);
    setForm({
      title: book.title,
      author: book.author,
      description: book.description,
      price: String(book.price),
      discount_percentage: String(book.discount_percentage || 0),
      is_visible: book.is_visible,
      cover_image: null,
      pdf_file: null
    });
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const formData = new FormData();
      
      // Build query params
      const params = new URLSearchParams({
        title: form.title,
        author: form.author,
        description: form.description,
        price: form.price,
        discount_percentage: form.discount_percentage,
        is_visible: form.is_visible
      });

      if (editBook) {
        // Update existing book
        if (form.cover_image) formData.append("cover_image", form.cover_image);
        if (form.pdf_file) formData.append("pdf_file", form.pdf_file);
        
        await axios.put(
          `${API}/admin/books/${editBook.id}?${params}`,
          formData,
          { ...authHeader(), headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" } }
        );
      } else {
        // Create new book
        if (!form.cover_image || !form.pdf_file) {
          setError("Cover image and PDF file are required");
          setSaving(false);
          return;
        }
        formData.append("cover_image", form.cover_image);
        formData.append("pdf_file", form.pdf_file);
        
        await axios.post(
          `${API}/admin/books?${params}`,
          formData,
          { ...authHeader(), headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" } }
        );
      }

      setShowModal(false);
      fetchBooks();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save book");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/books/${deleteId}`, authHeader());
      setDeleteId(null);
      fetchBooks();
    } catch (err) {
      console.error("Failed to delete book:", err);
    }
  };

  const toggleVisibility = async (book) => {
    try {
      const params = new URLSearchParams({ is_visible: !book.is_visible });
      await axios.put(`${API}/admin/books/${book.id}?${params}`, {}, authHeader());
      fetchBooks();
    } catch (err) {
      console.error("Failed to update visibility:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">Document Store</h1>
          <p className="text-gray-500 font-dm-sans">Manage your digital documents for sale</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-opacity"
        >
          <Plus size={18} />
          Add Document
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Total Documents" value={books.length} icon={BookOpen} color="cyan" />
        <StatCard title="Total Sales" value={sales?.total_sales || 0} icon={ShoppingCart} color="purple" />
        <StatCard title="Revenue" value={`₹${sales?.total_revenue || 0}`} icon={DollarSign} color="emerald" />
        <StatCard title="Visible Documents" value={books.filter(b => b.is_visible).length} icon={Eye} color="amber" />
      </div>

      {/* Documents Grid */}
      {books.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200">
          <BookOpen size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-outfit font-bold text-gray-900 mb-2">No Documents Yet</h3>
          <p className="text-gray-500 font-dm-sans mb-4">Upload your first digital document to start selling</p>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-cyan-500 text-white font-bold hover:bg-cyan-600"
          >
            Add Your First Document
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {books.map((book) => (
            <BookCard
              key={book.id}
              book={book}
              onEdit={openEdit}
              onDelete={setDeleteId}
              onToggleVisibility={toggleVisibility}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">{editBook ? "Edit Document" : "Add New Document"}</DialogTitle>
            <DialogDescription>
              {editBook ? "Update document details and files" : "Upload a new digital document for sale"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 text-sm">{error}</div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  className="w-full border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Author *</label>
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => setForm({...form, author: e.target.value})}
                  className="w-full border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Description *</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3}
                className="w-full border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Price (₹) *</label>
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({...form, price: e.target.value})}
                  className="w-full border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  min="0"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Discount %</label>
                <input
                  type="number"
                  value={form.discount_percentage}
                  onChange={(e) => setForm({...form, discount_percentage: e.target.value})}
                  className="w-full border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  min="0"
                  max="99"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_visible}
                    onChange={(e) => setForm({...form, is_visible: e.target.checked})}
                    className="w-4 h-4 text-cyan-500"
                  />
                  <span className="text-sm font-bold text-gray-700">Visible to public</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Cover Image {!editBook && "*"} <span className="font-normal text-gray-500">(JPG, PNG, WebP)</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 p-4 text-center hover:border-cyan-500 transition-colors">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(e) => setForm({...form, cover_image: e.target.files[0]})}
                    className="hidden"
                    id="cover-upload"
                  />
                  <label htmlFor="cover-upload" className="cursor-pointer">
                    <Upload size={24} className="mx-auto text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      {form.cover_image ? form.cover_image.name : "Click to upload cover"}
                    </span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  PDF File {!editBook && "*"}
                </label>
                <div className="border-2 border-dashed border-gray-300 p-4 text-center hover:border-cyan-500 transition-colors">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setForm({...form, pdf_file: e.target.files[0]})}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label htmlFor="pdf-upload" className="cursor-pointer">
                    <FileText size={24} className="mx-auto text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      {form.pdf_file ? form.pdf_file.name : "Click to upload PDF"}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 font-bold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 disabled:opacity-50"
              >
                {saving ? "Saving..." : editBook ? "Update Document" : "Create Document"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The document will be removed from the store.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
